/**
 * Сборка контрольного задания.
 *
 * Материал берётся из хвоста частотного списка — той же части, что отведена
 * под контрольный пул генератора упражнений. Так текст гарантированно не
 * пересекается с тем, что пользователь тренировал.
 */

import type { LanguageCode, LayoutId } from '@type2x/shared-contracts';
import { getWords } from '@type2x/corpora';
import { createRng } from '@type2x/exercise-generator';
import { RATING_CONFIG } from '@type2x/learning-engine';

import type { ControlTask } from '../contracts/control-test.js';

/**
 * Сколько слов входит в контрольный тест.
 *
 * Объём подобран так, чтобы даже медленный набор превысил минимальные
 * требования к засчитываемому результату.
 */
export const CONTROL_WORD_COUNT = 48;

/** Собирает контрольное задание из незнакомого материала. */
export function buildControlTask(
  language: LanguageCode,
  layoutId: LayoutId,
  seed: string,
  isRetentionCheck = false,
): ControlTask {
  const all = getWords(language, { minLength: 3 });
  // Вторая половина списка отведена под контроль и не используется в
  // тренировочных пулах.
  const pool = all.slice(Math.floor(all.length / 2)).map((word) => word.text);
  const shuffled = createRng(seed).shuffle(pool);

  const words = Array.from(
    { length: CONTROL_WORD_COUNT },
    (_unused, index) => shuffled[index % shuffled.length]!,
  );

  return { language, layoutId, text: words.join(' '), words, seed, isRetentionCheck };
}

/**
 * Хватит ли объёма задания, чтобы результат мог быть засчитан.
 *
 * Проверка нужна до показа: предлагать тест, который заведомо не пройдёт
 * минимальные требования, значит тратить время пользователя впустую.
 */
export function taskMeetsMinimumVolume(task: ControlTask): boolean {
  return [...task.text].length >= RATING_CONFIG.minVerifiedCharacters;
}
